import { FILE_STAGE, OVERDUE_FLAG, type LifecycleEntry } from '@ministry/ui/internal';

/**
 * The reconciliation point between `DESIGN_SYSTEM.md` §9.1's `FILE_STAGE` vocabulary and
 * the seven statuses `internal-api` actually implements
 * (`api/src/modules/file/domain/file.ts`'s `FILE_STATUSES`, confirmed by reading the
 * running server's code, not assumed from the design doc):
 *
 *   Design system (`FILE_STAGE` keys): registered, assigned, underReview, forwarded,
 *     approved, rejected, completed, archived.
 *   Server (`FILE_STATUSES`):           registered, reopened,  in_review,  forwarded,
 *     approved, rejected, completed.
 *
 * Two direct mismatches this file exists to absorb, so a screen renders a real badge
 * instead of an undefined `LifecycleEntry` (`entry.token` of `undefined` would throw
 * inside `StatusBadge`):
 *   - `in_review` (server) vs `underReview` (design system) — different strings, so a
 *     direct `FILE_STAGE[statusCode]` lookup silently returns `undefined`.
 *   - `reopened` has no design-system entry at all (`assigned` and `archived`, which the
 *     design system DOES define, are never returned as a `statusCode` — `archived` is a
 *     separate `archivedAt` timestamp on the file, not a status).
 *
 * Reported in this build's handback; the real fix is extending `packages/ui`'s
 * `FILE_STAGE`, which `web/internal` does not own.
 */
const REOPENED_ENTRY: LifecycleEntry = {
  token: 'warning',
  icon: 'calendar-clock',
  en: 'Re-opened',
  ne: 'पुनः खोलियो',
};

const UNKNOWN_STAGE_ENTRY: LifecycleEntry = {
  token: 'neutral',
  icon: 'archive-box',
  en: 'Unknown status',
  ne: 'अज्ञात स्थिति',
};

const STATUS_CODE_TO_STAGE: Record<string, LifecycleEntry> = {
  registered: FILE_STAGE.registered,
  in_review: FILE_STAGE.underReview,
  forwarded: FILE_STAGE.forwarded,
  approved: FILE_STAGE.approved,
  rejected: FILE_STAGE.rejected,
  completed: FILE_STAGE.completed,
  reopened: REOPENED_ENTRY,
};

/** Never throws. An unrecognised code (a status seeded after this bundle was built, or a genuine contract drift) degrades to a neutral, honestly-labelled badge rather than crashing the screen. */
export function fileStageEntry(statusCode: string): LifecycleEntry {
  return STATUS_CODE_TO_STAGE[statusCode] ?? UNKNOWN_STAGE_ENTRY;
}

/**
 * DESIGN_SYSTEM.md §10.1: "Section-wise workload by stage ... color = file stage token
 * ... reusing the lifecycle vocabulary here is the point." `BarChart`'s per-row `color`
 * override (`packages/ui`, this pass) needs an actual paintable value, not a Tailwind
 * class name — this is the one place a `LifecycleEntry.token` becomes the literal
 * `--ui-status-*-solid` custom property every badge/tile already resolves to, so a
 * "Forwarded" bar and a "Forwarded" badge are provably the same color, not two
 * hand-maintained hexes that can drift apart.
 */
const TOKEN_TO_CHART_COLOR: Record<LifecycleEntry['token'], string> = {
  info: 'var(--ui-status-info-solid)',
  good: 'var(--ui-status-good-solid)',
  warning: 'var(--ui-status-warning-solid)',
  serious: 'var(--ui-status-serious-solid)',
  critical: 'var(--ui-status-critical-solid)',
  neutral: 'var(--ui-status-neutral-solid)',
};

export function fileStageChartColor(statusCode: string): string {
  return TOKEN_TO_CHART_COLOR[fileStageEntry(statusCode).token];
}

export { OVERDUE_FLAG };

/**
 * Movement-timeline entries render the ACTION taken, not just the resulting status
 * (`comment`, for instance, changes nothing about the file's stage). `GET
 * /files/{id}/movements` already returns `labelNe`/`labelEn` per action
 * (`VERIFIED_RESPONSES.md`) — this only supplies the icon/color half of a
 * `LifecycleEntry`, never re-translates the label the server sent.
 */
const MOVEMENT_ACTION_TOKEN: Record<string, { token: LifecycleEntry['token']; icon: string }> = {
  register: { token: 'info', icon: 'document-plus' },
  forward: { token: 'info', icon: 'arrow-right' },
  approve: { token: 'good', icon: 'check-circle' },
  reject: { token: 'critical', icon: 'x-circle' },
  comment: { token: 'neutral', icon: 'eye' },
  complete: { token: 'good', icon: 'check-circle-double' },
  reassign: { token: 'warning', icon: 'user-check' },
  reopen: { token: 'warning', icon: 'calendar-clock' },
};

export function movementActionEntry(action: { code: string; labelNe: string; labelEn: string }): LifecycleEntry {
  const shape = MOVEMENT_ACTION_TOKEN[action.code] ?? { token: 'neutral' as const, icon: 'archive-box' };
  return { token: shape.token, icon: shape.icon, en: action.labelEn, ne: action.labelNe };
}

/**
 * `GET /reference/confidentiality-levels` (API_CONTRACT.md §13.9) is not implemented by
 * `internal-api` in this build — the route table
 * (`api/src/entrypoints/internal-api/routes.ts`) mounts only `notification`, `workflow`,
 * `file` and `identity`; no `reference` module exists yet. Hardcoded here from the exact
 * migration that seeds this table (`api/drizzle/0002_batch2_confidentiality_level.sql`)
 * as a stopgap so filters and tags show real bilingual labels instead of a raw code.
 *
 * This DRIFTS the moment a Super Admin edits the table without a client release —
 * precisely what that migration's own comment says must never matter ("a new level is an
 * INSERT, never a code change"). Flagged in the handback as a finding, not shipped as a
 * permanent design.
 */
export interface ConfidentialityLevelInfo {
  readonly code: string;
  readonly rank: number;
  readonly labelNe: string;
  readonly labelEn: string;
}

export const CONFIDENTIALITY_LEVELS: readonly ConfidentialityLevelInfo[] = [
  { code: 'public', rank: 10, labelNe: 'सार्वजनिक', labelEn: 'Public' },
  { code: 'internal', rank: 20, labelNe: 'आन्तरिक', labelEn: 'Internal' },
  { code: 'restricted', rank: 30, labelNe: 'प्रतिबन्धित', labelEn: 'Restricted' },
  { code: 'confidential', rank: 40, labelNe: 'गोप्य', labelEn: 'Confidential' },
];

export function confidentialityLabel(code: string, locale: 'en' | 'ne'): string {
  const entry = CONFIDENTIALITY_LEVELS.find((level) => level.code === code);
  if (!entry) return code;
  return locale === 'ne' ? entry.labelNe : entry.labelEn;
}

/** Attachments above `Internal` require the explicit, auditable download step (I8/ADR-0006) — this is the one predicate every screen uses to decide whether to show the lock treatment. */
export function isAboveInternalClearance(code: string): boolean {
  const entry = CONFIDENTIALITY_LEVELS.find((level) => level.code === code);
  return (entry?.rank ?? 0) > 20;
}

/**
 * `file_reassignment_reason` (DATA_MODEL.md #42, its seeded rows: `transferred`,
 * `on_leave`, `departed`, `workload_rebalance`, `other`) — a closed, server-enforced
 * vocabulary, not free text. **Verified against a running server as a genuine, real
 * defect this pass fixes**: both `ReassignModal` and the reassign step this build's
 * bulk-reassign feature adds used to default `reasonCode` to the free-typed string
 * `'workload'`, which does not exist in this table — every reassignment left at its
 * default value 422'd with `VALIDATION_FAILED: "No such active reassignment reason."`
 * (confirmed live: `POST /files/bulk-reassign` with `reasonCode: 'workload'` refuses
 * every item). No `/reference/...` endpoint exists to ask for this list (the same gap
 * `CONFIDENTIALITY_LEVELS` above already documents), so it is hardcoded from the exact
 * seed migration — same stopgap, same drift risk, same handback flag.
 */
export interface ReassignmentReasonInfo {
  readonly code: string;
  readonly labelNe: string;
  readonly labelEn: string;
}

export const REASSIGNMENT_REASONS: readonly ReassignmentReasonInfo[] = [
  { code: 'transferred', labelNe: 'सरुवा भएको', labelEn: 'Transferred' },
  { code: 'on_leave', labelNe: 'बिदामा रहेको', labelEn: 'On leave' },
  { code: 'departed', labelNe: 'सेवाबाट अलग भएको', labelEn: 'Departed' },
  { code: 'workload_rebalance', labelNe: 'काम बोझ पुनर्सन्तुलन', labelEn: 'Workload rebalance' },
  { code: 'other', labelNe: 'अन्य', labelEn: 'Other' },
];

/**
 * `REASSIGNMENT_REASONS[0].code` would be `string | undefined` under
 * `noUncheckedIndexedAccess` even though this array is a non-empty literal — derived once
 * here with an explicit (never-reached) fallback rather than a non-null assertion at
 * every call site.
 */
export const DEFAULT_REASSIGNMENT_REASON_CODE: string = REASSIGNMENT_REASONS[0]?.code ?? 'other';

/** The seven statuses a filter/status-select needs to offer, bilingual, without a reference endpoint to ask. */
export const FILTERABLE_STATUS_CODES = [
  'registered',
  'reopened',
  'in_review',
  'forwarded',
  'approved',
  'rejected',
  'completed',
] as const;
